import * as React from 'react';
import { FC, useCallback } from 'react';

import { useIntl } from 'react-intl';

import SanityBlockRenderer from 'components/sanity-block-renderer';
import useDialogModal, { DialogCb } from 'hooks/use-dialog-modal';

import { useFeatureScanAndPay } from './use-feature-scan-and-pay';

type UseCardDeletionConfirmationHook<T> = [FC, DialogCb<T>];

interface IUseCardDeletionConfirmationProps {
  onConfirmDelete(): void;
  onDismiss(): void;
}

/**
 *
 * useCardDeletionConfirmation configures the useDialogModal to
 *   render content confirming scan & pay card deletion
 *
 */
export function useCardDeletionConfirmation<T extends object>({
  onConfirmDelete,
  onDismiss,
}: IUseCardDeletionConfirmationProps): UseCardDeletionConfirmationHook<T> {
  const { formatMessage } = useIntl();
  const { scanAndPayContent } = useFeatureScanAndPay();

  const [
    ScanAndPayCardDeletionConfirmationDialog,
    openScanAndPayCardDeletionConfirmationDialog,
  ] = useDialogModal({
    allowDismiss: true,
    onCancel: () => {},
    onConfirm: () => {},
    showCancel: true,
  });

  const CardDeletionDialog = useCallback(() => {
    const DialogBody = (
      <SanityBlockRenderer
        content={
          scanAndPayContent?.paymentMethodDeletionModalText?.paymentMethodDeletionModalTextBody
            ?.locale
        }
      />
    );
    return (
      <ScanAndPayCardDeletionConfirmationDialog
        body={DialogBody}
        cancelLabel={formatMessage({ id: 'keepPaymentMethod' })}
        buttonLabel={formatMessage({ id: 'deleteAndDisable' })}
        heading={
          scanAndPayContent?.paymentMethodDeletionModalText?.paymentMethodDeletionModalTextHeader
            ?.locale ?? ''
        }
        onConfirm={onConfirmDelete}
        onDismiss={onDismiss}
      />
    );
  }, [formatMessage, onConfirmDelete, onDismiss, scanAndPayContent]);

  return [CardDeletionDialog, openScanAndPayCardDeletionConfirmationDialog];
}
